import {useEffect, useMemo, useState} from 'react';
import {createGlobalStyle, styled} from 'styled-components';

import {asMap, asNumber, asString} from '@shared/lib/utils/type_utils';
import {DoorState} from '@shared/models/api_models';

import {Flex} from '@shared_web/components/core2/flex';
import {notifyError} from '@shared_web/lib/notification';
import {Custom} from '@shared_web/lib/react';

import {Loader} from '@mavismart_frontend/components/loader';
import {translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {getUserLanguage, getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';

interface DoorStateViewProps {
  doorId: string;
}

export const DoorStateView: Custom<DoorStateViewProps, 'div'> = props => {
  const account = useAccount();
  const lang = getUserLanguage(account);
  const locale = getUserLocale(account);
  const {doorId, ...rest} = props;
  const [doorState, setDoorState] = useState<DoorState | undefined>(undefined);

  useEffect(() => {
    setDoorState(undefined);
    apiCall('POST /support-get-door-state', {doorId}).then(setDoorState).catch(notifyError);
  }, [doorId]);

  const state = useMemo(() => {
    const rawState = asMap(doorState) ?? {};
    const state = asMap(rawState['state']);
    const ts = asNumber(rawState['ts']);
    const simId = asString(rawState['simId']);
    if (state === undefined || ts === undefined) {
      return;
    }
    const lines = JSON.stringify(state, undefined, 2)
      .split('\n')
      .map(l =>
        l
          .replaceAll(
            /(?<highlight>"(?:MI\.00[356789])": [^,\n]+)/gu,
            (val: string) => `<span class="highlight">${val}</span>`
          )
          .replaceAll(' ', '&nbsp;')
          .replaceAll('<span&nbsp;class', '<span class')
      );
    return {lines, ts, simId};
  }, [doorState]);

  if (state === undefined) {
    return <StyledLoader />;
  }

  return (
    <Flex $column $gap="m" $width="100%" {...rest}>
      <Flex $gap="s" $align="baseline" $justify="space-between">
        <Title>{translate('door_state_view_title', lang)}</Title>
        <TitleDate>{new Date(state.ts).toLocaleString(locale)}</TitleDate>
      </Flex>
      <JsonLines>
        {state.lines.map((l, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: index is the key voluntarily
          // biome-ignore lint/security/noDangerouslySetInnerHtml: safe content
          <PreLine key={i} dangerouslySetInnerHTML={{__html: l}} />
        ))}
      </JsonLines>
      <JsonLines>
        <PreLine>Sim ID: {state.simId}</PreLine>
      </JsonLines>
      <HighlightStyles />
    </Flex>
  );
};

DoorStateView.displayName = 'DoorStateView';

const Title = styled.div`
  font-size: 22px;
`;

const TitleDate = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const StyledLoader = styled(Loader)`
  height: 100px;
`;

const JsonLines = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 6px;
  overflow: auto;
  background-color: #00000011;
  font-family: monospace;
`;

const PreLine = styled.div`
  white-space: nowrap;
`;

const HighlightStyles = createGlobalStyle`
  .highlight {
    background: #ffff0077;
    color: #000000dd;
    border-radius: 3px;
  }
`;
